import { Button, DateInput, Select, SelectOptionType } from '@octano/global-ui';
import dayjs from 'dayjs';
import { useEffect, useMemo, useRef } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Col, Row } from 'reactstrap';
import RemoteSearchSelect from '../../../components/form/RemoteSearchSelect';
import { useSearchResponsible } from '../../../hooks/remoteSearchSelect/useSearchResponsible';
import useSelectOptions from '../../../hooks/useSelectOptions';
import { useValidations } from '../../../hooks/useValidations';
import { PeriodType } from '../../../types/sectionMaintainerTypes';
import { ModalityCodesEnum } from '../enums/modality-codes.enum';
import MaintainerScheduleClassroomLoader, {
  MaintainerScheduleClassroomLoaderFilters,
} from './MaintainerScheduleClassroomLoader';
import MaintainerScheduleClassroomSelector from './MaintainerScheduleClassroomSelector';
import { ScheduleSectionFormData } from './ModalMaintainerSchedulePre';

const prefix = 'sectionsMaintainer.update.modalClass';

type Props = {
  sectionId: number | string;
  action: 'Update' | 'Create';
  activityOptions: SelectOptionType[];
  campusOptions: SelectOptionType[];
  moduleStartOptions: SelectOptionType[];
  moduleEndOptions: SelectOptionType[];
  responsibleOptions: SelectOptionType[];
  modalityOptions: SelectOptionType[];
  onClose: () => void;
  period?: PeriodType;
};

const MaintainerScheduleFormFields = ({
  sectionId,
  action,
  activityOptions,
  campusOptions,
  moduleStartOptions,
  moduleEndOptions,
  modalityOptions,
  onClose,
  period,
}: Props) => {
  const isUpdate = action === 'Update';

  const { t } = useTranslation();
  const { daysOptions } = useSelectOptions();
  const searchResponsible = useSearchResponsible(sectionId);
  const {
    msgValidations,
    validateBeforeDate,
    validateTextNotEmpty,
  } = useValidations();

  const {
    control,
    watch,
    setValue,
    trigger,
  } = useFormContext<ScheduleSectionFormData>();

  const [startDate, endDate] = watch(['startDate', 'endDate']);
  const [startModule, endModule] = watch(['startModule', 'endModule']);
  const [day, selectedCampus, modality] = watch(['day', 'campus', 'modality']);

  const optional = (value: string) => {
    if (modality?.deliveryMode === ModalityCodesEnum.PR) {
      return validateTextNotEmpty(value);
    }
    return undefined;
  };

  const filters = useMemo<MaintainerScheduleClassroomLoaderFilters>(() => {
    const getValue = <T extends any>(value: SelectOptionType | null) =>
      value?.value ? (value.value as T) : undefined;
    return {
      campusId: getValue(selectedCampus),
      day: getValue(day),
      startModuleId: getValue(startModule),
      endModuleId: getValue(endModule),
      startDate: startDate,
      endDate: endDate,
    };
  }, [day, endDate, selectedCampus, startModule, endModule, startDate]);

  // Al cambiar el modulo de término se vuelve a validar el módulo de inicio
  useEffect(() => {
    if (endModule) {
      trigger('startModule');
    }
  }, [endModule, trigger]);

  // Al cambiar la fecha de término se vuelve a validar la fecha de inicio
  useEffect(() => {
    if (endDate) {
      trigger('startDate');
    }
  }, [endDate, trigger]);

  // Al cambiar o borrar el campus seleccionado
  // se elimina la selección de sala
  const prevCampusRef = useRef(selectedCampus);
  useEffect(() => {
    const prevCampus = prevCampusRef.current;
    if (prevCampus?.value !== selectedCampus?.value) {
      setValue('classroom', null);
      prevCampusRef.current = selectedCampus;
    }
  }, [selectedCampus, setValue]);

  return (
    <>
      <Row className="pb-2">
        <Col xs={12} lg={4}>
          <Select
            label={t(`${prefix}.activity`)}
            name="activity"
            options={activityOptions}
            control={control}
            rules={{ required: msgValidations.required }}
            shouldUnregister
          />
        </Col>
        <Col xs={12} lg={4}>
          <Select
            label={t(`${prefix}.campus`)}
            name="campus"
            options={campusOptions}
            control={control}
            rules={{ validate: { require: optional } }}
            shouldUnregister
            disabled={modality?.deliveryMode === ModalityCodesEnum.OL}
          />
        </Col>
        <Col xs={12} lg={4}>
          <Select
            label={t(`${prefix}.modality`)}
            name="modality"
            options={modalityOptions}
            control={control}
            shouldUnregister
          />
        </Col>
        <Col xs={12} lg={4}>
          <RemoteSearchSelect
            name="responsible"
            label={t(`${prefix}.responsible`)}
            filterOptions={searchResponsible}
            control={control}
            rules={{ required: msgValidations.required }}
            shouldUnregister
          />
        </Col>
        <Col xs={12} lg={4}>
          <Select
            label={t(`${prefix}.day`)}
            name="day"
            options={daysOptions}
            control={control}
            rules={{
              required: msgValidations.required,
            }}
            shouldUnregister
            disabled={isUpdate}
          />
        </Col>
        <Col xs={12} lg={4}>
          <Select
            label={t(`${prefix}.startModule`)}
            name="startModule"
            options={moduleStartOptions}
            control={control}
            rules={{
              required: msgValidations.required,
              validate: (value: SelectOptionType) => {
                if (value && endModule) {
                  const indexStart = moduleStartOptions.findIndex(
                    (mod) => mod.value === value.value,
                  );
                  const indexEnd = moduleStartOptions.findIndex(
                    (mod) => mod.value === endModule.value,
                  );
                  if (
                    indexStart >= 0 &&
                    indexEnd >= 0 &&
                    indexStart > indexEnd
                  ) {
                    return msgValidations.inValidBeforeModule;
                  }
                }
                return undefined;
              },
            }}
            shouldUnregister
          />
        </Col>
        <Col xs={12} lg={4}>
          <Select
            label={t(`${prefix}.endModule`)}
            name="endModule"
            options={moduleEndOptions}
            control={control}
            rules={{ required: msgValidations.required }}
            shouldUnregister
          />
        </Col>

        <Col xs={12} lg={4}>
          <DateInput
            label={t(`${prefix}.startDate`)}
            name="startDate"
            control={control}
            rules={{
              required: msgValidations.required,
              validate: validateBeforeDate(endDate),
            }}
            minDate={
              period?.startDate ? dayjs(period.startDate).toDate() : undefined
            }
            maxDate={
              period?.endDate ? dayjs(period.endDate).toDate() : undefined
            }
            shouldUnregister
          />
        </Col>
        <Col xs={12} lg={4}>
          <DateInput
            label={t(`${prefix}.endDate`)}
            name="endDate"
            control={control}
            rules={{ required: msgValidations.required }}
            minDate={
              period?.startDate ? dayjs(period.startDate).toDate() : undefined
            }
            maxDate={
              period?.endDate ? dayjs(period.endDate).toDate() : undefined
            }
            shouldUnregister
          />
        </Col>

        {/* selector de salas disponible solo en modalidad presencial */}
        {modality?.deliveryMode === ModalityCodesEnum.PR ? (
          <Col xs={12} lg={4}>
            <MaintainerScheduleClassroomLoader filters={filters}>
              <MaintainerScheduleClassroomSelector />
            </MaintainerScheduleClassroomLoader>
          </Col>
        ) : null}

        <Col xs={12} className="pb-2 text-center">
          {watch('campus') &&
            !watch('classroom') &&
            modality?.deliveryMode === ModalityCodesEnum.PR && (
              <p className="text-danger">{t(`${prefix}.obs`)}</p>
            )}
        </Col>
        <Col xs={12}>
          <Row>
            <Col xs={12} lg={4} className="pb-2 pb-lg-0 ml-auto">
              <Button
                text={t('common.actions.close')}
                outlined
                onClick={() => {
                  onClose();
                }}
                fullwidth
              />
            </Col>
            <Col xs={12} lg={4}>
              <Button
                type="submit"
                text={t('common.actions.confirm')}
                fullwidth
              />
            </Col>
          </Row>
        </Col>
      </Row>
    </>
  );
};

export default MaintainerScheduleFormFields;
